import { ofetch, type FetchOptions } from 'ofetch';
import { env, API_TIMEOUT_MS } from '@noor/config';
import { ApiError } from './error';
import { normalizeErrorBody } from './error-body';
import { readAuthToken, handleUnauthorized, refreshAuthToken } from './auth-token';
import { readLocale } from './locale';
import { logApiRequest, logApiResponse, resolveRequestUrl } from './request-log';
import { unwrapData } from './unwrap-data';

/**
 * HTTP client for LEGACY mode — the toggleable reproduction of the old
 * merchant panel. It is deliberately a SEPARATE client from `http`, not a
 * variant of it, because the old panel talks to a different backend with
 * incompatible conventions:
 *
 * | | `http` (new) | `legacyHttp` (old) |
 * |---|---|---|
 * | base | `VITE_API_BASE_URL` | `VITE_LEGACY_API_BASE_URL` |
 * | envelope | `{data, meta}` — unwrapped | raw body, no envelope |
 * | refresh | auth-service `POST /api/v1/auth/refresh` | same shared auth system |
 *
 * Sharing one client would mean branching on the sub-app inside every
 * interceptor; keeping them apart means legacy mode cannot regress the new
 * app's request path, and deleting legacy mode later is deleting files.
 *
 * Legacy mode reuses the session the new app already established. A 401
 * therefore follows the same refresh-token path as `http`: refresh through the
 * shared auth backend, retry the legacy request once, then redirect only if
 * the shared session is genuinely dead.
 */
const client = ofetch.create({
  baseURL: env.VITE_LEGACY_API_BASE_URL,
  retry: 0,
  timeout: API_TIMEOUT_MS,
  onRequest({ request, options }) {
    const headers = new Headers(options.headers);

    // Same session as the new app — legacy mode is a UI toggle, not a
    // separate login. (The old panel also kept a `business_token` for account
    // switching; that flow is part of the unported auth surface.)
    const token = readAuthToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    // The old panel sent the language as `Accept-Language`, not the `x-locale`
    // header the new gateway uses. Localized fields come back wrong (or
    // default to Russian) without it.
    if (!headers.has('Accept-Language')) headers.set('Accept-Language', readLocale());

    // Platform marker so the backend can tell web sessions apart from mobile
    // ones. A caller-supplied value wins.
    if (!headers.has('X-Platform')) headers.set('X-Platform', 'web');

    options.headers = headers;

    // Dev-only wire log (URL + headers) — silent in prod and under tests.
    logApiRequest(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_LEGACY_API_BASE_URL),
      headers,
    );
  },
  onResponse({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_LEGACY_API_BASE_URL),
      response.status,
    );
  },
  onResponseError({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_LEGACY_API_BASE_URL),
      response.status,
    );
    const body = normalizeErrorBody(response._data);
    throw new ApiError({
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' must fall through
      message: body.message || response.statusText || 'Request failed',
      status: response.status,
      ...(body.code !== undefined ? { code: body.code } : {}),
      ...(body.details !== undefined ? { details: body.details } : {}),
    });
  },
});

const authClient = ofetch.create({
  baseURL: env.VITE_AUTH_API_URL,
  retry: 0,
  timeout: API_TIMEOUT_MS,
  onRequest({ request, options }) {
    // Dev-only wire log (URL + headers) — silent in prod and under tests.
    logApiRequest(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_AUTH_API_URL),
      new Headers(options.headers),
    );
  },
  onResponse({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_AUTH_API_URL),
      response.status,
    );
  },
  onResponseError({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_AUTH_API_URL),
      response.status,
    );
    const body = normalizeErrorBody(response._data);
    throw new ApiError({
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' must fall through
      message: body.message || response.statusText || 'Request failed',
      status: response.status,
      ...(body.code !== undefined ? { code: body.code } : {}),
      ...(body.details !== undefined ? { details: body.details } : {}),
    });
  },
});

async function callRefresh(refreshToken: string) {
  const raw = await authClient<unknown>('/api/v1/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken },
  });
  const payload = unwrapData(raw) as Partial<{
    access_token: string;
    refresh_token: string;
  }> | null;
  const accessToken = payload?.access_token;
  const newRefreshToken = payload?.refresh_token;
  if (typeof accessToken !== 'string' || typeof newRefreshToken !== 'string') {
    throw new ApiError({ message: 'Malformed refresh response', status: 500 });
  }
  return { accessToken, refreshToken: newRefreshToken };
}

async function withAuthRetry<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (!(error instanceof ApiError) || !error.isUnauthorized) throw error;

    const newToken = await refreshAuthToken(callRefresh);
    if (newToken === null) {
      handleUnauthorized();
      throw error;
    }

    try {
      return await request();
    } catch (retryError) {
      if (retryError instanceof ApiError && retryError.isUnauthorized) {
        handleUnauthorized();
      }
      throw retryError;
    }
  }
}

/**
 * Typed verb helpers for the legacy backend. Mirrors `http`'s shape so the
 * legacy slices read like the rest of the codebase — but note the responses
 * are NOT unwrapped: the old API returns its payload at the top level, and
 * several endpoints wrap the rows in their own ad-hoc `{data, count}` object
 * that callers parse with a Zod schema.
 */
export const legacyHttp = {
  get: <T>(url: string, query?: Record<string, unknown>) =>
    withAuthRetry(() => client<T>(url, { method: 'GET', ...(query ? { query } : {}) })),
  // `body as never` mirrors `http`'s verb helpers: ofetch types `body` as
  // `BodyInit | Record<string, any>`, which an `unknown` parameter cannot
  // satisfy, and the runtime serializes any JSON-shaped value fine.
  post: <T>(url: string, body?: unknown) =>
    withAuthRetry(() => client<T>(url, { method: 'POST', body: body as never })),
  put: <T>(url: string, body?: unknown) =>
    withAuthRetry(() => client<T>(url, { method: 'PUT', body: body as never })),
  patch: <T>(url: string, body?: unknown) =>
    withAuthRetry(() => client<T>(url, { method: 'PATCH', body: body as never })),
  delete: <T>(url: string, body?: unknown) =>
    withAuthRetry(() => client<T>(url, { method: 'DELETE', body: body as never })),
  /** GET a binary response (broadcast attachment). Returns the raw Blob. */
  getBlob: (
    url: string,
    options?: Omit<FetchOptions<'blob'>, 'responseType' | 'method'>,
  ) =>
    withAuthRetry(() =>
      client<Blob, 'blob'>(url, { ...options, method: 'GET', responseType: 'blob' }),
    ),
};
